import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Database, Users, Activity, AlertCircle, CheckCircle, Clock,
  ArrowRight, Building2, Server, Zap, TrendingUp, Calendar,
  MapPin, FileText, Settings, Plus, Edit, MoreVertical, Upload, X, Image,
  CheckSquare, Image as ImageIcon, MessageSquare, ThumbsUp, MoreHorizontal
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { CostcoLogo, HighJumpLogo, SqlServerLogo, SqlHealthProLogo } from "@/components/logos";
import { useClient } from "@/contexts/ClientContext";
import { ClientAwareLink } from "@/components/ClientAwareLink";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DemoWelcomeCard } from "@/components/DemoWelcomeCard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";

interface ClientMetrics {
  totalDatabases: number;
  activeProjects: number;
  criticalIssues: number;
  lastAssessment: string;
  healthScore: number;
  currentPhase: string;
  nextMilestone: string;
}

interface Client {
  id: string;
  name: string;
  code: string;
  warehouseSystem: string;
  databaseDialect: string;
  isActive: boolean;
  location: string;
  industry: string;
  metrics: ClientMetrics;
  status: 'active' | 'discovery' | 'implementation' | 'monitoring';
  logoColor: string;
  logoUrl?: string;
  logoBase64?: string;
}

export default function Home() {
  const [clients, setClients] = useState<Client[]>([]);
  const [location, navigate] = useLocation();
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setSelectedClient } = useClient();

  useEffect(() => {
    // Load clients from API
    const loadClients = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Fetching clients from /api/clients...');

        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4500/api'}/clients`);
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers.get('content-type'));

        if (response.ok) {
          const apiClients = await response.json();
          console.log('Loaded clients from API:', apiClients.length);

          // Transform API data to match our Client interface
          const transformedClients: Client[] = apiClients.map((client: any) => ({
            id: client.id.toString(),
            name: client.name,
            code: client.code,
            warehouseSystem: client.warehouseSystem || 'Unknown System',
            databaseDialect: client.databaseDialect || 'SQL Server',
            isActive: client.isActive !== false,
            location: `${client.headquartersCity || 'Unknown'}, ${client.headquartersState || 'Unknown'}`,
            industry: client.industry || 'Unknown Industry',
            status: client.status || 'active',
            logoColor: client.brandPrimaryColor || 'bg-shiftleft-600',
            logoUrl: client.logoUrl,
            logoBase64: client.logoBase64,
            metrics: {
              totalDatabases: client.totalDatabases || 0,
              activeProjects: Math.floor(Math.random() * 10) + 1, // Still mock for now
              criticalIssues: client.criticalIssuesCount || 0,
              lastAssessment: client.lastHealthCheck ? new Date(client.lastHealthCheck).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              healthScore: client.currentHealthScore || 75,
              currentPhase: 'Discovery', // Still mock for now
              nextMilestone: 'Next Review - TBD' // Still mock for now
            }
          }));

          setClients(transformedClients);
        } else {
          throw new Error(`Failed to load clients: ${response.status}`);
        }
      } catch (error) {
        console.error('Error loading clients:', error);
        setError(error instanceof Error ? error.message : 'Failed to load clients');

        // Fallback to demo data
        setClients([
          {
            id: 'acme-demo',
            name: 'Acme Corporation',
            code: 'ACME',
            warehouseSystem: 'HighJump WMS 2022',
            databaseDialect: 'SQL Server 2019',
            isActive: true,
            location: 'Seattle, WA',
            industry: 'E-commerce',
            status: 'active',
            logoColor: 'bg-shiftleft-600',
            metrics: {
              totalDatabases: 12,
              activeProjects: 3,
              criticalIssues: 5,
              lastAssessment: new Date().toISOString().split('T')[0],
              healthScore: 78,
              currentPhase: 'Optimization',
              nextMilestone: 'Q1 Performance Review - Feb 15'
            }
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'discovery': return 'bg-shiftleft-100 text-shiftleft-800 border-shiftleft-200';
      case 'implementation': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'monitoring': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setIsEditModalOpen(true);
  };

  const refreshClients = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4500/api'}/clients`);
      if (response.ok) {
        const apiClients = await response.json();
        const transformedClients: Client[] = apiClients.map((client: any) => ({
          id: client.id.toString(),
          name: client.name,
          code: client.code,
          warehouseSystem: client.warehouseSystem || 'Unknown System',
          databaseDialect: client.databaseDialect || 'SQL Server',
          isActive: client.isActive !== false,
          location: `${client.headquartersCity || 'Unknown'}, ${client.headquartersState || 'Unknown'}`,
          industry: client.industry || 'Unknown Industry',
          status: client.status || 'active',
          logoColor: client.brandPrimaryColor || 'bg-shiftleft-600',
          logoUrl: client.logoUrl,
          logoBase64: client.logoBase64,
          metrics: {
            totalDatabases: client.totalDatabases || 0,
            activeProjects: Math.floor(Math.random() * 10) + 1,
            criticalIssues: client.criticalIssuesCount || 0,
            lastAssessment: client.lastHealthCheck ? new Date(client.lastHealthCheck).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            healthScore: client.currentHealthScore || 75,
            currentPhase: 'Discovery',
            nextMilestone: 'Next Review - TBD'
          }
        }));
        setClients(transformedClients);
      }
    } catch (error) {
      console.error('Error refreshing clients:', error);
    }
  };

  const handleClientClick = (client: Client) => {
    // Store client in session context
    setSelectedClient(client);
    // Navigate to client page
    navigate(`/clients/${client.code}`);
  };

  const handleSaveClient = async (updatedClient: Client) => {
    try {
      // Prepare data for API - match the database schema
      const updateData = {
        name: updatedClient.name,
        code: updatedClient.code,
        industry: updatedClient.industry,
        warehouseSystem: updatedClient.warehouseSystem,
        databaseDialect: updatedClient.databaseDialect,
        logoBase64: updatedClient.logoBase64,
        logoUrl: updatedClient.logoUrl,
        // Parse location back to city/state if needed
        headquartersCity: updatedClient.location.split(',')[0]?.trim(),
        headquartersState: updatedClient.location.split(',')[1]?.trim(),
      };

      console.log('Saving client with logo:', {
        id: updatedClient.id,
        name: updatedClient.name,
        hasLogo: !!(updatedClient.logoBase64 || updatedClient.logoUrl),
        logoSize: updatedClient.logoBase64 ? updatedClient.logoBase64.length : 0
      });

      // Make API call to update client
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4500/api'}/clients/${updatedClient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update client');
      }

      const savedClient = await response.json();

      console.log('Client saved successfully:', {
        id: savedClient.id,
        name: savedClient.name,
        hasLogo: !!(savedClient.logoBase64 || savedClient.logoUrl)
      });

      // Update local state with saved data, preserving our UI-specific fields
      setClients(prev => prev.map(c =>
        c.id === updatedClient.id
          ? {
            ...updatedClient,
            logoBase64: savedClient.logoBase64,
            logoUrl: savedClient.logoUrl,
            name: savedClient.name,
            code: savedClient.code,
            industry: savedClient.industry,
            warehouseSystem: savedClient.warehouseSystem,
            databaseDialect: savedClient.databaseDialect
          }
          : c
      ));

      setIsEditModalOpen(false);
      setEditingClient(null);

      // Refresh the client list to get the latest data
      await refreshClients();

      // Show success message
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error saving client:', error);
      alert(`Failed to save client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const createItems = [
    { icon: FileText, label: "Document", description: "Write stuff", color: "text-shiftleft-500", bgColor: "bg-shiftleft-50" },
    { icon: CheckSquare, label: "Task", description: "Track work", color: "text-green-500", bgColor: "bg-green-50" },
    { icon: ImageIcon, label: "Post", description: "Share ideas", color: "text-orange-500", bgColor: "bg-orange-50" },
    { icon: MessageSquare, label: "Message", description: "Start a chat", color: "text-purple-500", bgColor: "bg-purple-50" },
  ];

  return (
    <ErrorBoundary>
      <WorkspaceLayout>
        <div className="max-w-6xl mx-auto bg-white min-h-screen pb-20">

          {/* Notch/Cover Image Area */}
          <div className="group relative h-24 sm:h-36 md:h-48 w-full bg-[#F0F5F9] border-b border-[#E1E1E0]">
            <div className="absolute bottom-4 right-4 sm:right-8 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
              <Button variant="ghost" size="sm" className="bg-white/50 hover:bg-white text-xs h-7 hidden sm:inline-flex">Change cover</Button>
              <Button variant="ghost" size="sm" className="bg-white/50 hover:bg-white text-xs h-7 hidden sm:inline-flex">Reposition</Button>
            </div>
          </div>

          <div className="px-4 sm:px-8 md:px-12 lg:px-24 relative">

            {/* Title & Toolbar */}
            <div className="mt-4 sm:mt-6 mb-6 sm:mb-8 group">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#37352F] tracking-tight">Workspace Home</h1>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[#787774] text-xs sm:text-sm">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Last updated by Admin today at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>

                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="text-[#787774]">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                  <Button className="bg-[#2383E2] hover:bg-[#1C73C9] text-white shadow-sm h-8 px-3 text-sm font-medium">
                    <Plus className="w-4 h-4 mr-1.5" />
                    New
                  </Button>
                </div>
              </div>

              {/* Description / Quote Block */}
              <div className="mt-6 pl-4 border-l-2 border-l-[#D9D9D9] py-1">
                <p className="text-[16px] text-[#37352F] italic">
                  "Manage your enterprise system assessments, findings, and remediation projects in one unified workspace."
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-[#E1E1E0] my-8" />

            {/* Quick Actions (Callouts) */}
            <div className="mb-12">
              <h3 className="text-[12px] font-semibold text-[#787774] uppercase tracking-wider mb-3 select-none">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {createItems.map((item) => (
                  <div key={item.label} className="flex p-3 rounded hover:bg-[#F7F7F5] cursor-pointer transition-colors border border-transparent hover:border-[#E1E1E0] group">
                    <div className={`mr-3 mt-0.5 w-6 h-6 flex items-center justify-center rounded-sm ${item.bgColor} bg-opacity-50`}>
                      <item.icon className="w-4 h-4 text-[#37352F]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#37352F] text-[14px] underline-offset-2 group-hover:underline">{item.label}</div>
                      <div className="text-[12px] text-[#787774] mt-0.5">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dashboard Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">

              {/* Main Content: Database Gallery View */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center justify-between border-b border-[#E1E1E0] pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-[#37352F]">Projects Database</span>
                    <span className="text-[12px] text-[#787774] bg-[#F1F1EF] px-1.5 rounded ml-1">{clients.length} projects</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-6 text-[#787774] text-xs hover:bg-[#E1E1E0]">Filter</Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[#787774] text-xs hover:bg-[#E1E1E0]">Sort</Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[#787774] text-xs hover:bg-[#E1E1E0]"><Plus className="w-3 h-3" /></Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Clients as Notion Cards */}
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleClientClick(client)}
                      className="group border border-[#E1E1E0] rounded-sm overflow-hidden hover:bg-[#F7F7F5] cursor-pointer shadow-sm transition-all"
                    >
                      {/* Card Cover Preview */}
                      <div className="h-24 bg-[#F7F7F5] border-b border-[#E1E1E0] relative flex items-center justify-center overflow-hidden">
                        {client.logoBase64 || client.logoUrl ? (
                          <img src={client.logoBase64 || client.logoUrl} alt="" className="h-16 object-contain" />
                        ) : (
                          <div className="text-4xl">🏢</div>
                        )}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 bg-white/80 hover:bg-white"><MoreHorizontal className="w-3 h-3 text-[#37352F]" /></Button>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[18px]">📄</span>
                          <span className="font-medium text-[#37352F] text-[14px] truncate">{client.name}</span>
                        </div>

                        {/* Properties */}
                        <div className="space-y-1 pt-1">
                          <div className="flex items-start text-[12px] gap-2">
                            <span className="w-20 text-[#787774] shrink-0 truncate">Status</span>
                            <Badge variant="outline" className={`font-normal rounded-sm px-1.5 py-0 border-0 ${client.status === 'active' ? 'bg-[#D3E5EF] text-[#183347]' :
                              client.status === 'discovery' ? 'bg-[#E8DEEE] text-[#412454]' :
                                'bg-[#FDECC8] text-[#402C1B]'
                              }`}>
                              {client.status}
                            </Badge>
                          </div>
                          <div className="flex items-start text-[12px] gap-2">
                            <span className="w-20 text-[#787774] shrink-0 truncate">Location</span>
                            <span className="text-[#37352F] truncate">{client.location}</span>
                          </div>
                          <div className="flex items-start text-[12px] gap-2">
                            <span className="w-20 text-[#787774] shrink-0 truncate">Health</span>
                            <div className="flex-1 flex items-center gap-2">
                              <div className="h-1.5 flex-1 bg-[#E1E1E0] rounded-full overflow-hidden">
                                <div className="h-full bg-[#2383E2] rounded-full" style={{ width: `${client.metrics.healthScore}%` }} />
                              </div>
                              <span className="text-[#787774]">{client.metrics.healthScore}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* New Item Placeholder */}
                  <div onClick={() => alert("Create new project")} className="border border-dashed border-[#E1E1E0] rounded-sm h-[240px] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F7F7F5] transition-colors text-[#787774] group">
                    <Plus className="w-8 h-8 mb-2 group-hover:text-[#37352F] transition-colors" />
                    <span className="text-[14px] font-medium group-hover:text-[#37352F]">New</span>
                  </div>
                </div>
              </div>

              {/* Sidebar: Activity & Views */}
              <div className="space-y-8">

                {/* Linked Views */}
                <div>
                  <h3 className="text-[12px] font-semibold text-[#787774] uppercase tracking-wider mb-2 select-none">Views</h3>
                  <div className="space-y-1">
                    {['Board View', 'List View', 'Calendar', 'Timeline', 'Gallery'].map(view => (
                      <div key={view} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F1F1EF] cursor-pointer text-[#37352F] text-[14px]">
                        <Database className="w-4 h-4 text-[#787774]" />
                        <span>{view}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity Log */}
                <div>
                  <h3 className="text-[12px] font-semibold text-[#787774] uppercase tracking-wider mb-3 select-none">Recent Activity</h3>
                  <div className="space-y-4">
                    {[
                      { user: "Mason Clay", action: "updated", target: "Q1 Roadmap", time: "2h ago", avatar: "MC" },
                      { user: "Rose Compás", action: "created", target: "5 new findings", time: "4h ago", avatar: "RC" },
                      { user: "System", action: "completed", target: "Health Check", time: "Yesterday", avatar: "SYS" },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3 text-[13px]">
                        <Avatar className="w-6 h-6 border bg-[#F7F7F5]">
                          <AvatarFallback className="text-[9px] text-[#787774]">{item.avatar}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-[#37352F] leading-snug">
                            <span className="font-medium">{item.user}</span> {item.action} <span className="underline decoration-[#E1E1E0]">{item.target}</span>
                          </p>
                          <span className="text-[11px] text-[#9B9A97]">{item.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Edit Client Modal */}
          <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Edit Client</DialogTitle>
              </DialogHeader>
              {editingClient && (
                <EditClientForm
                  client={editingClient}
                  onSave={handleSaveClient}
                  onCancel={() => setIsEditModalOpen(false)}
                />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </WorkspaceLayout>
    </ErrorBoundary>
  );
}

// Edit Client Form Component
function EditClientForm({
  client,
  onSave,
  onCancel
}: {
  client: Client;
  onSave: (client: Client) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState<Client>(client);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const updateFormData = (field: keyof Client, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateMetrics = (field: keyof ClientMetrics, value: any) => {
    setFormData(prev => ({
      ...prev,
      metrics: { ...prev.metrics, [field]: value }
    }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        updateFormData('logoBase64', base64String);
        updateFormData('logoUrl', base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    updateFormData('logoBase64', undefined);
    updateFormData('logoUrl', undefined);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target?.result as string;
          updateFormData('logoBase64', base64String);
          updateFormData('logoUrl', base64String);
        };
        reader.readAsDataURL(file);
      } else {
        alert('Please drop an image file');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Client Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            placeholder="Client Name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="code">Client Code</Label>
          <Input
            id="code"
            value={formData.code}
            onChange={(e) => updateFormData('code', e.target.value)}
            placeholder="CLIENT_CODE"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => updateFormData('location', e.target.value)}
            placeholder="City, State"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="industry">Industry</Label>
          <Input
            id="industry"
            value={formData.industry}
            onChange={(e) => updateFormData('industry', e.target.value)}
            placeholder="Industry"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="warehouseSystem">Warehouse System</Label>
          <Input
            id="warehouseSystem"
            value={formData.warehouseSystem}
            onChange={(e) => updateFormData('warehouseSystem', e.target.value)}
            placeholder="System Name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="databaseDialect">Database</Label>
          <Select
            value={formData.databaseDialect}
            onValueChange={(value) => updateFormData('databaseDialect', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select database" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SQL Server">SQL Server</SelectItem>
              <SelectItem value="PostgreSQL">PostgreSQL</SelectItem>
              <SelectItem value="MySQL">MySQL</SelectItem>
              <SelectItem value="Oracle">Oracle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value: any) => updateFormData('status', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="discovery">Discovery</SelectItem>
            <SelectItem value="implementation">Implementation</SelectItem>
            <SelectItem value="monitoring">Monitoring</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logo Upload Section */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Company Logo</h4>
        <div className="flex items-start gap-4">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            {formData.logoBase64 || formData.logoUrl ? (
              <div className="relative">
                <img
                  src={formData.logoBase64 || formData.logoUrl}
                  alt="Company Logo"
                  className="w-20 h-20 object-contain border border-gray-200 rounded-lg bg-white p-2"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                  onClick={removeLogo}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div
                className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('logo-upload')?.click()}
              >
                <Image className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="logo-upload" className="cursor-pointer">
                <Button type="button" variant="outline" size="sm" asChild>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Logo
                  </span>
                </Button>
              </Label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              {formData.logoBase64 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeLogo}
                >
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Upload a company logo (PNG, JPG, SVG) or drag & drop. Max size: 5MB. Recommended: 200x200px or square aspect ratio.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Metrics</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="totalDatabases">Total Databases</Label>
            <Input
              id="totalDatabases"
              type="number"
              value={formData.metrics.totalDatabases}
              onChange={(e) => updateMetrics('totalDatabases', parseInt(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="activeProjects">Active Projects</Label>
            <Input
              id="activeProjects"
              type="number"
              value={formData.metrics.activeProjects}
              onChange={(e) => updateMetrics('activeProjects', parseInt(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="criticalIssues">Critical Issues</Label>
            <Input
              id="criticalIssues"
              type="number"
              value={formData.metrics.criticalIssues}
              onChange={(e) => updateMetrics('criticalIssues', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="healthScore">Health Score</Label>
            <Input
              id="healthScore"
              type="number"
              min="0"
              max="100"
              value={formData.metrics.healthScore}
              onChange={(e) => updateMetrics('healthScore', parseInt(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currentPhase">Current Phase</Label>
            <Input
              id="currentPhase"
              value={formData.metrics.currentPhase}
              onChange={(e) => updateMetrics('currentPhase', e.target.value)}
              placeholder="Current Phase"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="nextMilestone">Next Milestone</Label>
          <Input
            id="nextMilestone"
            value={formData.metrics.nextMilestone}
            onChange={(e) => updateMetrics('nextMilestone', e.target.value)}
            placeholder="Next Milestone"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Save Changes
        </Button>
      </div>
    </form>
  );
}
